import { Button, DialogTrigger, Flex, Link, Text } from '@adobe/react-spectrum';
import CheckmarkCircle from '@spectrum-icons/workflow/CheckmarkCircle';
import React, { useContext } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { EcAcquiredDialog, WithSkeleton } from '@exchange-frontends/components';
import { useMediaQuery, useRootHref } from '@exchange-frontends/custom-hooks';

import useConsent from '../../../../../actions/entitlementApi/selectors/ecAppDetails/useConsent';
import { ROUTES } from '../../../../../constants';
import { useStore } from '../../../store';
import { AppDetailsContext } from '../AppDetails';
import { HeaderButtonsContext } from './HeaderButtons';

/**
 * @param {Object} props
 * @param {boolean} [props.isTrial]
 * @param {string} [props.manageEcLinkId]
 */
const ManageButton = ({ isTrial, manageEcLinkId }) => {
  const { id } = useParams();
  const manageLink = useRootHref(ROUTES.MANAGE ?? '');
  const isMobileSized = useMediaQuery('(max-width: 500px)');
  const { selectedOrg, fetchCreativeCloudLink } = useContext(HeaderButtonsContext);
  const { cloud } = useContext(AppDetailsContext);
  const detailScreen = useStore((state) => state.detailScreen);

  const isAdmin = selectedOrg?.role === 'ADMIN';
  const consentNeeded = detailScreen?.appType === 'S2S';
  const consentQuery = useConsent({ enabled: consentNeeded }, { appId: id, orgId: selectedOrg?.code });
  const shouldRenderSkeleton = consentQuery.isLoading && consentNeeded;
  const wasConsentGiven = consentQuery.data?.consent?.status === 'ALLOWED';

  return (
    <Flex wrap="wrap" gap="size-125" justifyContent="end">
      <Flex gap="size-50" alignItems="center">
        <CheckmarkCircle color="positive" size="S" />
        <Text>{isTrial ? 'Trial Acquired' : 'Acquired'}</Text>
      </Flex>
      {cloud === 'cc' && !isMobileSized && (
        <Link isQuiet onPress={() => fetchCreativeCloudLink('view')} data-launchid="Manage">
          Manage
        </Link>
      )}
      {cloud === 'ec' && (
        <WithSkeleton shouldRender={!shouldRenderSkeleton} borderRadius="16px" width={135} height={30}>
          {wasConsentGiven || !consentNeeded ? (
            <Link isQuiet data-launchid="Manage" id={manageEcLinkId}>
              <RouterLink to={`${manageLink}/apps/ec/${selectedOrg.code}/${detailScreen?.appType}/view/${id}`}>
                Manage
              </RouterLink>
            </Link>
          ) : (
            <DialogTrigger>
              <Button variant="accent">Begin installation</Button>
              {(close) => (
                <EcAcquiredDialog
                  isAdmin={isAdmin}
                  close={close}
                  dialogVariant="beginInstallation"
                  requiredProducts={detailScreen?.hostApps}
                  optionalProducts={detailScreen?.optionalProducts}
                  appId={id}
                  orgId={selectedOrg?.code}
                  appType={detailScreen?.appType}
                  clientId={consentQuery.data?.consent?.clientId}
                  scopes={detailScreen?.scopeDescription}
                />
              )}
            </DialogTrigger>
          )}
        </WithSkeleton>
      )}
    </Flex>
  );
};

export default ManageButton;
